import {
  addDecimal,
  cmpDecimal,
  isDecimal,
  mulDecimal,
  roundDecimalString,
  subDecimal,
} from '@biovet/ui';
import {
  defaultVoucherDate,
  hasFiscalCalendar,
  findAccount,
  hasSubAccounts,
  resolvePeriod,
  splitPostingAccount,
  subAccountsOf,
  type Period,
} from './reference-data.js';
import type { Side, VoucherTypeConfig } from './voucher-config.js';


export type VoucherStatus = 'NEW' | 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'POSTED';
export type ApprovalState = 'NOT_REQUIRED' | 'PENDING' | 'APPROVED';
export type Currency = string;
export type Role = 'accountant' | 'manager' | 'owner';

export interface FxContext {
  currency: Currency;
  fxRate: string;
  baseCurrency: Currency;
}

export interface VoucherLineDraft {
  id: string;
  accountCode: string;
  subAccountCode: string;
  narration: string;
  debit: string;
  credit: string;
  partyCode: string;
}

export function postingAccountOf(line: VoucherLineDraft): string {
  return line.subAccountCode || line.accountCode;
}

export interface VoucherDraft {
  voucherNo: string | null;
  voucherDate: string;
  narration: string;
  contraAccountCode: string;
  lines: VoucherLineDraft[];
  status: VoucherStatus;
  approvalState: ApprovalState;
}

let lineSeq = 0;

export function emptyLine(): VoucherLineDraft {
  lineSeq += 1;
  return {
    id: `line-${lineSeq}`,
    accountCode: '',
    subAccountCode: '',
    narration: '',
    debit: '',
    credit: '',
    partyCode: '',
  };
}

export function emptyDraft(today = defaultVoucherDate()): VoucherDraft {
  return {
    voucherNo: null,
    voucherDate: today,
    narration: '',
    contraAccountCode: '',
    lines: [emptyLine(), emptyLine(), emptyLine()],
    status: 'NEW',
    approvalState: 'NOT_REQUIRED',
  };
}

export function duplicateDraft(draft: VoucherDraft): VoucherDraft {
  return {
    ...draft,
    voucherNo: null,
    status: 'NEW',
    approvalState: 'NOT_REQUIRED',
    voucherDate: defaultVoucherDate(),
    lines: draft.lines.map((line) => ({ ...line, id: emptyLine().id })),
  };
}

export function amountFieldFor(config: VoucherTypeConfig): 'debit' | 'credit' {
  return config.analysisSide === 'Dr' ? 'debit' : 'credit';
}


export function parseAmount(raw: string): { value: string | null; invalid: boolean } {
  const text = raw.replace(/[\s,]/g, '');
  if (text === '' || text === '-' || text === '.' || text === '-.') {
    return { value: null, invalid: false };
  }
  if (!isDecimal(text)) return { value: null, invalid: true };
  return { value: text, invalid: false };
}

export function toBaseExact(amountTxn: string, fxRate: string): string {
  return roundDecimalString(mulDecimal(amountTxn, fxRate), 4);
}


export interface GlLine {
  lineNo: number;
  accountCode: string;
  side: Side;
  amountTxn: string;
  amountBase: string;
  partyCode: string;
  narration: string;
  generated: boolean;
}

interface EnteredLine {
  line: VoucherLineDraft;
  debit: string | null;
  credit: string | null;
  debitBase: string;
  creditBase: string;
}

export interface VoucherSummary {
  entered: EnteredLine[];
  glLines: GlLine[];
  totalDebitTxn: string;
  totalCreditTxn: string;
  totalDebitBase: string;
  totalCreditBase: string;
  differenceBase: string;
  differenceTxn: string;
  balanced: boolean;
  filledLines: number;
  contraSplit: boolean;
}

export function summarise(
  draft: VoucherDraft,
  config: VoucherTypeConfig,
  fx: FxContext,
): VoucherSummary {
  const rate = isDecimal(fx.fxRate) ? fx.fxRate : '1';

  const entered: EnteredLine[] = draft.lines.map((line) => {
    const debit = parseAmount(line.debit).value;
    const credit = parseAmount(line.credit).value;
    return {
      line,
      debit,
      credit,
      debitBase: debit ? toBaseExact(debit, rate) : '0',
      creditBase: credit ? toBaseExact(credit, rate) : '0',
    };
  });

  const filled = entered.filter((e) => e.debit !== null || e.credit !== null);

  const analysisLines: GlLine[] = filled.map((e, i) => {
    const posting = postingAccountOf(e.line);
    return {
      lineNo: i + 1,
      accountCode: posting,
      side: e.debit !== null ? 'Dr' : 'Cr',
      amountTxn: (e.debit ?? e.credit) as string,
      amountBase: e.debit !== null ? e.debitBase : e.creditBase,
      partyCode: partyFor(posting, e.line.partyCode),
      narration: e.line.narration,
      generated: false,
    } satisfies GlLine;
  });

  const contra = config.contra
    ? buildContraLines(draft, config, filled)
    : { lines: [] as GlLine[], split: false };

  const all = [...analysisLines, ...contra.lines];
  const ordered = [
    ...all.filter((l) => l.side === 'Dr'),
    ...all.filter((l) => l.side === 'Cr'),
  ].map((l, i) => ({ ...l, lineNo: i + 1 }));

  const totalDebitTxn = sumWhere(ordered, 'Dr', 'amountTxn');
  const totalCreditTxn = sumWhere(ordered, 'Cr', 'amountTxn');
  const totalDebitBase = sumWhere(ordered, 'Dr', 'amountBase');
  const totalCreditBase = sumWhere(ordered, 'Cr', 'amountBase');

  return {
    entered,
    glLines: ordered,
    totalDebitTxn,
    totalCreditTxn,
    totalDebitBase,
    totalCreditBase,
    differenceBase: subDecimal(totalDebitBase, totalCreditBase),
    differenceTxn: subDecimal(totalDebitTxn, totalCreditTxn),
    balanced: cmpDecimal(totalDebitBase, totalCreditBase) === 0 && ordered.length > 0,
    filledLines: filled.length,
    contraSplit: contra.split,
  };
}

function partyFor(accountCode: string, partyCode: string): string {
  if (!partyCode) return '';
  return findAccount(accountCode)?.requiresParty ? partyCode : '';
}

function buildContraLines(
  draft: VoucherDraft,
  config: VoucherTypeConfig,
  filled: EnteredLine[],
): { lines: GlLine[]; split: boolean } {
  const contra = config.contra;
  if (!contra || filled.length === 0) return { lines: [], split: false };

  const amountOf = (e: EnteredLine) => (e.debit ?? e.credit) as string;
  const baseOf = (e: EnteredLine) => (e.debit !== null ? e.debitBase : e.creditBase);

  return {
    lines: filled.map((e) => ({
      accountCode: draft.contraAccountCode,
      side: contra.side,
      partyCode: '',
      generated: true,
      lineNo: 0,
      amountTxn: amountOf(e),
      amountBase: baseOf(e),
      narration: e.line.narration,
    })),
    split: filled.length > 1,
  };
}

function sumWhere(lines: GlLine[], side: Side, key: 'amountTxn' | 'amountBase'): string {
  return lines.filter((l) => l.side === side).reduce((acc, l) => addDecimal(acc, l[key]), '0');
}


export type IssueSeverity = 'error' | 'warning';

export interface Issue {
  severity: IssueSeverity;
  message: string;
  field?: string;
  lineId?: string;
  enforcedBy?: string;
}

export interface Validation {
  issues: Issue[];
  errors: Issue[];
  warnings: Issue[];
  period: Period | undefined;
  amountBase: string;
  requiresApproval: boolean;
  canSaveDraft: boolean;
  canSubmit: boolean;
  canPost: boolean;
  postBlockers: string[];
}


export interface VoucherPayloadLine {
  accountCode: string;
  side: Side;
  amount: string;
  partyCode?: string;
  narration?: string;
}

export interface VoucherPayload {
  type: string;
  voucherNo: string | null;
  voucherDate: string;
  narration: string;
  partyCode: string | null;
  currency: Currency;
  fxRate: string;
  branchCode: string | null;
  lines: VoucherPayloadLine[];
}

export function toPayload(
  type: string,
  draft: VoucherDraft,
  summary: VoucherSummary,
  fx: FxContext,
  branchCode: string | null,
): VoucherPayload {
  return {
    type,
    voucherNo: draft.voucherNo,
    voucherDate: draft.voucherDate,
    narration: draft.narration,
    partyCode: null,
    currency: fx.currency,
    fxRate: fx.currency === fx.baseCurrency ? '1' : fx.fxRate,
    branchCode,
    lines: summary.glLines.map((line) => ({
      accountCode: line.accountCode,
      side: line.side,
      amount: line.amountTxn,
      partyCode: line.partyCode || undefined,
      narration: line.narration || undefined,
    })),
  };
}

export interface LoadedLine {
  accountCode: string;
  side: Side;
  amount: string;
  partyCode: string | null;
  narration: string | null;
}

export interface LoadedVoucher {
  voucherNo: string;
  voucherDate: string;
  status: string;
  narration: string | null;
  partyCode: string | null;
  lines: LoadedLine[];
}

export function draftFromLoaded(loaded: LoadedVoucher, config: VoucherTypeConfig): VoucherDraft {
  const contraSide = config.contra?.side;
  const contraLines = contraSide ? loaded.lines.filter((l) => l.side === contraSide) : [];
  const analysis = contraSide ? loaded.lines.filter((l) => l.side !== contraSide) : loaded.lines;

  return {
    voucherNo: loaded.voucherNo,
    voucherDate: loaded.voucherDate,
    narration: loaded.narration ?? '',
    contraAccountCode: contraLines[0]?.accountCode ?? '',
    lines:
      analysis.length > 0
        ? analysis.map((line) => ({
            ...emptyLine(),
            ...splitPostingAccount(line.accountCode),
            narration: line.narration ?? '',
            debit: line.side === 'Dr' ? line.amount : '',
            credit: line.side === 'Cr' ? line.amount : '',
            partyCode: line.partyCode ?? '',
          }))
        : [emptyLine()],
    status: normaliseStatus(loaded.status),
    approvalState: loaded.status === 'PENDING_APPROVAL' ? 'PENDING' : 'NOT_REQUIRED',
  };
}

function normaliseStatus(status: string): VoucherStatus {
  switch (status) {
    case 'DRAFT':
    case 'PENDING_APPROVAL':
    case 'APPROVED':
    case 'POSTED':
      return status;
    default:
      return 'POSTED';
  }
}

export function validate(
  draft: VoucherDraft,
  config: VoucherTypeConfig,
  summary: VoucherSummary,
  role: Role,
  fx: FxContext,
): Validation {
  const issues: Issue[] = [];
  const period = resolvePeriod(draft.voucherDate);
  const error = (message: string, extra: Partial<Issue> = {}) =>
    issues.push({ severity: 'error', message, ...extra });
  const warn = (message: string, extra: Partial<Issue> = {}) =>
    issues.push({ severity: 'warning', message, ...extra });

  if (!draft.voucherDate) {
    error('Voucher date is required.', { field: 'voucherDate' });
  } else if (!hasFiscalCalendar()) {
    error('No open financial year. Open one under Setup → Financial Year & Periods.', {
      field: 'voucherDate',
      enforcedBy: 'resolve_period()',
    });
  } else if (!period) {
    error(
      `Nothing open covers ${draft.voucherDate}. Open the financial year it falls in under Setup → Financial Year & Periods.`,
      { field: 'voucherDate', enforcedBy: 'resolve_period()' },
    );
  } else if (period.status === 'CLOSED') {
    error(
      `${period.name} is closed. A draft may sit in a closed period; posting into one is refused.`,
      { field: 'voucherDate', enforcedBy: 'trg_vouchers_period_lock' },
    );
  } else if (period.status === 'FUTURE') {
    error(`${period.name} has not been opened yet.`, {
      field: 'voucherDate',
      enforcedBy: 'trg_vouchers_period_lock',
    });
  }

  if (!isDecimal(fx.fxRate) || cmpDecimal(fx.fxRate, '0') <= 0) {
    error('The FX rate in the top bar is not a positive number — fix it there.', {
      field: 'fxRate',
    });
  }

  if (config.contra) {
    const account = findAccount(draft.contraAccountCode);
    if (!draft.contraAccountCode) {
      error(`${config.contra.label} is required.`, { field: 'contraAccountCode' });
    } else if (!account) {
      error('Unknown account.', { field: 'contraAccountCode' });
    } else if (!account.isLeaf) {
      error(`${account.code} is a group account — a posting must target a leaf.`, {
        field: 'contraAccountCode',
        enforcedBy: 'trg_voucher_lines_derive',
      });
    } else if (account.control !== config.contra.kind) {
      error(`${account.code} is not a ${config.contra.kind.toLowerCase()} account.`, {
        field: 'contraAccountCode',
      });
    } else if (
      account.fcyCurrency &&
      fx.currency !== account.fcyCurrency &&
      fx.currency !== fx.baseCurrency
    ) {
      error(
        `${account.code} holds ${account.fcyCurrency} — a ${fx.currency} line cannot post to it.`,
        { field: 'contraAccountCode', enforcedBy: 'trg_voucher_lines_derive' },
      );
    } else if (!account.fcyCurrency && fx.currency !== fx.baseCurrency) {
      warn(
        `${account.code} is a ${fx.baseCurrency} account. A ${fx.currency} entry posts its base equivalent and leaves no foreign-currency balance to revalue.`,
        { field: 'contraAccountCode' },
      );
    }
  }

  if (!draft.narration.trim()) {
    warn(
      'Narration is empty. It heads the printed voucher — the General Ledger shows each line’s own narration instead.',
      { field: 'narration' },
    );
  }

  const minimum = config.shape === 'JOURNAL' ? 2 : 1;
  if (summary.filledLines < minimum) {
    error(
      minimum === 2
        ? 'A journal voucher needs at least one debit and one credit line.'
        : 'Enter at least one line.',
    );
  }

  const seen = new Set<string>();

  for (const entry of summary.entered) {
    const { line, debit, credit } = entry;
    const hasAmount = debit !== null || credit !== null;
    const touched = hasAmount || line.accountCode !== '' || line.narration !== '';
    if (!touched) continue;

    if (parseAmount(line.debit).invalid || parseAmount(line.credit).invalid) {
      error('Amount is not a number.', { lineId: line.id });
      continue;
    }

    if (!line.accountCode) {
      error('Account is required on every line.', { lineId: line.id });
    } else if (hasSubAccounts(line.accountCode) && !line.subAccountCode) {
      const count = subAccountsOf(line.accountCode).length;
      error(
        `${line.accountCode} has ${count} sub-account${count === 1 ? '' : 's'}, so it takes no lines of its own — choose the one this posts to in the Sub account column.`,
        { lineId: line.id, enforcedBy: 'trg_voucher_lines_derive' },
      );
    } else {
      const account = findAccount(postingAccountOf(line));
      if (!account) {
        error('Unknown account.', { lineId: line.id });
      } else {
        if (!account.isLeaf) {
          error(
            `${account.code} ${account.name} is a group account — a posting must target a leaf.`,
            { lineId: line.id, enforcedBy: 'trg_voucher_lines_derive' },
          );
        }
        if (config.shape === 'JOURNAL' && account.control && !account.allowDirectPosting) {
          error(
            `${account.code} ${account.name} is a control account — post through its sub-ledger document, not a JV.`,
            { lineId: line.id, enforcedBy: 'trg_voucher_lines_derive' },
          );
        }
        if (account.requiresParty && !line.partyCode) {
          error(
            `${account.code} ${account.name} keeps a sub-ledger, so this line needs a party — otherwise the balance cannot be reconciled to a customer or supplier.`,
            { lineId: line.id },
          );
        }
        if (
          account.fcyCurrency &&
          fx.currency !== account.fcyCurrency &&
          fx.currency !== fx.baseCurrency
        ) {
          error(
            `${account.code} holds ${account.fcyCurrency} — a ${fx.currency} line cannot post to it.`,
            { lineId: line.id, enforcedBy: 'trg_voucher_lines_derive' },
          );
        }
      }
    }

    if (!hasAmount) {
      error('Line has an account but no amount.', { lineId: line.id });
    } else if (debit !== null && credit !== null) {
      error('A line is either a debit or a credit, never both.', { lineId: line.id });
    } else if (cmpDecimal((debit ?? credit) as string, '0') <= 0) {
      error('Amount must be greater than zero — put it on the other side instead of negating it.', {
        lineId: line.id,
      });
    }

    const posting = postingAccountOf(line);
    if (hasAmount && posting) {
      if (seen.has(posting)) {
        warn('Same account as an earlier line — merge them or give each a narration.', {
          lineId: line.id,
        });
      }
      seen.add(posting);
    }
  }

  const unnarrated = summary.entered.filter(
    (e) => (e.debit !== null || e.credit !== null) && !e.line.narration.trim(),
  ).length;
  if (unnarrated > 0) {
    warn(
      `${unnarrated} line${unnarrated === 1 ? '' : 's'} carry no narration, so ${unnarrated === 1 ? 'it reaches' : 'they reach'} the General Ledger with none — the header's narration heads the voucher and is not copied down.`,
    );
  }

  if (summary.filledLines >= minimum && !summary.balanced) {
    if (config.shape === 'JOURNAL' && cmpDecimal(summary.differenceTxn, '0') === 0) {
      error(
        `Sides agree in ${fx.currency} but differ by ${summary.differenceBase} in base: each line converts and rounds to 4 dp independently. Adjust a line so the base totals meet.`,
        { enforcedBy: 'trg_vouchers_balance (DEFERRABLE)' },
      );
    } else {
      error(`Debits and credits differ by ${summary.differenceBase} in base currency.`, {
        enforcedBy: 'trg_vouchers_balance (DEFERRABLE)',
      });
    }
  }

  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  const amountBase = summary.totalDebitBase;
  const overThreshold = cmpDecimal(amountBase, config.approvalThreshold) > 0;
  const requiresApproval = overThreshold && role === 'accountant';

  const postBlockers: string[] = [];
  if (errors.length > 0) {
    postBlockers.push(`${errors.length} validation ${errors.length === 1 ? 'error' : 'errors'}`);
  }
  if (period && period.status !== 'OPEN') postBlockers.push(`${period.name} is not open`);
  if (requiresApproval) {
    postBlockers.push('above your approval threshold — submit it for a checker');
  }
  if (draft.status === 'POSTED') postBlockers.push('already posted');

  return {
    issues,
    errors,
    warnings,
    period,
    amountBase,
    requiresApproval,
    canSaveDraft: draft.status !== 'POSTED' && summary.filledLines > 0,
    canSubmit: draft.status !== 'POSTED' && errors.length === 0 && requiresApproval,
    canPost: postBlockers.length === 0 && summary.filledLines > 0,
    postBlockers,
  };
}
